/* @jsxImportSource react */
'use client';

import { type ContextType, useContext, useMemo } from 'react';

import { Col } from '@opendoor/atoms/box';
import { FormContext } from '@opendoor/atoms/field';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { useSelector } from '@tanstack/react-store';

/** The connected TanStack form instance carried by atoms' `<Form.Root>`. */
type AuthForm = NonNullable<ContextType<typeof FormContext>>;

export interface AuthFormErrorProps {
  /**
   * Server-side failure message(s) — invalid credentials, lockout, resend
   * limits, social-auth failures. Rendered first, above any field errors.
   * (The GROWTH-2588 module context is the other intended source; wiring it in
   * is that shell's job — this component takes the messages either way.)
   */
  serverErrors?: string | string[] | null;
  className?: string;
  'data-testid'?: string;
}

/**
 * The single form-level error region for AccountForm V2. All error text lands
 * here — server failures first, then client-side field-validation messages in
 * form order — deduped into one `role="alert"` region above the method/field
 * stack. Offending fields keep their red ring and `aria-invalid`; only the
 * message text lives here (never inline at the field), so multi-field screens
 * have a single, predictable place for every message.
 *
 * Field errors are read straight off the TanStack form via atoms' `FormContext`
 * (this renders inside `<Form.Root>`); with no form present it still renders
 * server errors alone, for method-only screens like the social-auth failure.
 */
export const AuthFormError = ({
  serverErrors,
  className,
  'data-testid': dataTestId = 'auth-form-error',
}: AuthFormErrorProps) => {
  const form = useContext(FormContext);
  const serverMessages = normalizeMessages(serverErrors);

  if (form) {
    return (
      <ConnectedAuthFormError
        form={form}
        serverMessages={serverMessages}
        className={className}
        data-testid={dataTestId}
      />
    );
  }
  return (
    <AuthFormErrorRegion
      messages={dedupe(serverMessages)}
      className={className}
      data-testid={dataTestId}
    />
  );
};

AuthFormError.displayName = 'AuthFormError';

function ConnectedAuthFormError({
  form,
  serverMessages,
  className,
  'data-testid': dataTestId,
}: {
  form: AuthForm;
  serverMessages: string[];
  className?: string;
  'data-testid': string;
}) {
  // Match the field-level UX: hold errors until the first submit attempt, then
  // show and live-update. Driven by the form's `submissionAttempts`, same as
  // atoms' `useConnectedField`.
  const submissionAttempts = useSelector(form.store, (state) => state.submissionAttempts);
  const fieldMeta = useSelector(form.store, (state) => state.fieldMeta);
  // Object-level validators (e.g. a password-match `.refine()`) produce
  // path-less issues that never reach a field's meta — they live in the
  // form-level `errors`. Read those too, or that whole class of submit-blocking
  // failure would render nothing.
  const formErrors = useSelector(form.store, (state) => state.errors);

  const messages = useMemo(() => {
    if (submissionAttempts === 0) return dedupe(serverMessages);
    return dedupe([
      ...serverMessages,
      ...collectFieldMessages(fieldMeta),
      ...extractMessages(formErrors),
    ]);
  }, [submissionAttempts, fieldMeta, formErrors, serverMessages]);

  return <AuthFormErrorRegion messages={messages} className={className} data-testid={dataTestId} />;
}

function AuthFormErrorRegion({
  messages,
  className,
  'data-testid': dataTestId,
}: {
  messages: string[];
  className?: string;
  'data-testid': string;
}) {
  if (messages.length === 0) return null;
  return (
    <Col
      // role="alert" already implies aria-live="assertive"; don't downgrade it —
      // this region carries sign-in failures, lockouts, and validation errors.
      role="alert"
      data-testid={dataTestId}
      className={cn('w-full gap-1 rounded-2xl bg-od-bg-error-secondary px-4 py-3', className)}
    >
      {messages.map((message) => (
        <Text key={message} as="span" className="text-body-sm text-od-content-error">
          {message}
        </Text>
      ))}
    </Col>
  );
}

/** First occurrence wins, order preserved — stable keys and no repeats. */
function dedupe(messages: string[]): string[] {
  return [...new Set(messages)];
}

function normalizeMessages(input: string | string[] | null | undefined): string[] {
  if (!input) return [];
  return (Array.isArray(input) ? input : [input]).filter(
    (message): message is string => typeof message === 'string' && message.length > 0,
  );
}

/**
 * Pull user-facing messages off every registered field's meta, in registration
 * (form) order. The empty-string key is TanStack's form-level bucket, not a
 * field — skip it here so form-level errors render once, in order, via
 * `extractMessages(state.errors)`.
 */
function collectFieldMessages(fieldMeta: AuthForm['store']['state']['fieldMeta']): string[] {
  const messages: string[] = [];
  for (const [name, meta] of Object.entries(fieldMeta)) {
    if (name === '') continue;
    messages.push(...extractMessages(meta?.errors));
  }
  return messages;
}

/**
 * Recursively pull user-facing strings out of TanStack's error shapes: raw
 * strings, Zod issue objects (`{ message }`), arrays of either, and the
 * path-keyed maps that form-level `errors` wraps them in (`{ "": [issue] }`).
 * Anything without a string message is dropped — the region only renders
 * self-identifying copy.
 */
function extractMessages(value: unknown): string[] {
  if (value == null) return [];
  if (typeof value === 'string') return value.length > 0 ? [value] : [];
  if (Array.isArray(value)) return value.flatMap(extractMessages);
  if (typeof value === 'object') {
    if ('message' in value && typeof value.message === 'string') {
      return value.message.length > 0 ? [value.message] : [];
    }
    return Object.values(value).flatMap(extractMessages);
  }
  return [];
}
